import { Modal, Select, Text } from '@viapeople/components'
import { useMemo, useState } from 'react'
import { useFormBuilderStore } from '../store'
import type { ComponentDefinition } from '../store/componentRegistry'
import type { FormComponentName } from '../types'
import { DynamicPropertyEditor } from './propertyEditors/DynamicPropertyEditor'

interface BulkEditModalProps {
	isOpen: boolean
	onClose: () => void
}

// Deep diff utility to find exactly what changed
function getDeepChanges(
	initial: Record<string, unknown>,
	updated: Record<string, unknown>,
	path = '',
): Array<{ path: string; value: unknown }> {
	const changes: Array<{ path: string; value: unknown }> = []

	// Check all keys in updated object
	Object.keys(updated).forEach((key) => {
		const currentPath = path ? `${path}.${key}` : key
		const initialValue = initial[key]
		const updatedValue = updated[key]

		// If values are the same, skip
		if (JSON.stringify(initialValue) === JSON.stringify(updatedValue)) {
			return
		}

		// If both are objects/arrays, recurse
		if (
			typeof updatedValue === 'object' &&
			updatedValue !== null &&
			typeof initialValue === 'object' &&
			initialValue !== null &&
			!Array.isArray(updatedValue)
		) {
			const nestedChanges = getDeepChanges(
				initialValue as Record<string, unknown>,
				updatedValue as Record<string, unknown>,
				currentPath,
			)
			changes.push(...nestedChanges)
		} else if (Array.isArray(updatedValue) && Array.isArray(initialValue)) {
			// For arrays, check each element
			const maxLength = Math.max(updatedValue.length, initialValue.length)
			for (let i = 0; i < maxLength; i++) {
				const itemPath = `${currentPath}[${i}]`
				if (i >= initialValue.length) {
					// New item added
					changes.push({ path: itemPath, value: updatedValue[i] })
				} else if (i >= updatedValue.length) {
					// Item removed (mark as undefined)
					changes.push({ path: itemPath, value: undefined })
				} else if (JSON.stringify(initialValue[i]) !== JSON.stringify(updatedValue[i])) {
					// Item changed
					if (typeof updatedValue[i] === 'object' && updatedValue[i] !== null) {
						const nestedChanges = getDeepChanges(
							initialValue[i] as Record<string, unknown>,
							updatedValue[i] as Record<string, unknown>,
							itemPath,
						)
						changes.push(...nestedChanges)
					} else {
						changes.push({ path: itemPath, value: updatedValue[i] })
					}
				}
			}
		} else {
			// Primitive value or complete replacement
			changes.push({ path: currentPath, value: updatedValue })
		}
	})

	return changes
}

// Apply deep changes to a target object
function applyDeepChanges(
	target: Record<string, unknown>,
	changes: Array<{ path: string; value: unknown }>,
): Record<string, unknown> {
	const result = JSON.parse(JSON.stringify(target)) // Deep clone

	changes.forEach(({ path, value }) => {
		const parts = path.split(/\.|\[|\]/).filter(Boolean)
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let current: any = result

		for (let i = 0; i < parts.length - 1; i++) {
			const part = parts[i]
			const nextPart = parts[i + 1]
			const isArrayIndex = /^\d+$/.test(nextPart)

			if (!(part in current)) {
				current[part] = isArrayIndex ? [] : {}
			}
			current = current[part]
		}

		const lastPart = parts[parts.length - 1]
		if (value === undefined && Array.isArray(current)) {
			// Remove array element
			current.splice(Number(lastPart), 1)
		} else {
			current[lastPart] = value
		}
	})

	return result
}

export function BulkEditModal({ isOpen, onClose }: BulkEditModalProps) {
	const formComponents = useFormBuilderStore((state) => state.formComponents)
	const getComponentDefinition = useFormBuilderStore((state) => state.getComponentDefinition)
	const updateComponentProps = useFormBuilderStore((state) => state.updateComponentProps)

	const [selectedComponentType, setSelectedComponentType] = useState<string>('')
	const [editedProps, setEditedProps] = useState<Record<string, unknown>>({})
	const [initialProps, setInitialProps] = useState<Record<string, unknown>>({})
	const [deepChanges, setDeepChanges] = useState<Array<{ path: string; value: unknown }>>([])

	// Get all unique component types in the form
	const componentTypeOptions = useMemo(() => {
		if (!formComponents || formComponents.length === 0) {
			return []
		}

		const typesMap = new Map<string, { name: string; displayName: string }>()

		const collectTypes = (components: typeof formComponents) => {
			components.forEach((comp) => {
				// Skip dividers as they don't have meaningful editable properties
				if (comp.name === 'divider') {
					if (comp.children && comp.children.length > 0) {
						collectTypes(comp.children)
					}
					return
				}

				const isSection = (comp.custom as { isSection?: boolean })?.isSection
				const definition = getComponentDefinition(comp.name, isSection)

				if (definition) {
					// Create unique key for component type
					const key = isSection ? `${comp.name}-section` : comp.name
					if (!typesMap.has(key)) {
						typesMap.set(key, {
							name: comp.name,
							displayName: isSection ? `${definition.displayName} (Section)` : definition.displayName,
						})
					}
				}

				if (comp.children && comp.children.length > 0) {
					collectTypes(comp.children)
				}
			})
		}

		collectTypes(formComponents)

		return Array.from(typesMap.entries())
			.map(([key, value]) => ({
				value: key,
				label: value.displayName,
			}))
			.sort((a, b) => a.label.localeCompare(b.label))
	}, [formComponents, getComponentDefinition])

	// Get the component definition for the selected type
	const selectedDefinition = useMemo((): ComponentDefinition | undefined => {
		if (!selectedComponentType) return undefined

		const isSection = selectedComponentType.includes('-section')
		const componentName = selectedComponentType.replace('-section', '') as FormComponentName

		return getComponentDefinition(componentName, isSection)
	}, [selectedComponentType, getComponentDefinition])

	// Top-level property names that opt out of bulk edit on the currently selected component type.
	// Their per-instance values must not be overwritten across every component on the form.
	const nonBulkEditableProps = useMemo(() => {
		const set = new Set<string>()
		selectedDefinition?.propertyDefinitions?.forEach((def) => {
			if (def.bulkEditable === false) set.add(def.name)
		})
		return set
	}, [selectedDefinition])

	const handlePropertyChange = (_: unknown, value: unknown) => {
		const typedValue = value as Record<string, unknown>
		setEditedProps(typedValue)

		// Get deep changes - exact paths and values that changed; drop any that touch a property
		// flagged bulkEditable: false so the preview and the apply step both honor the opt-out.
		const changes = getDeepChanges(initialProps, typedValue).filter((change) => {
			const topLevelKey = change.path.split(/\.|\[/, 1)[0]
			return !nonBulkEditableProps.has(topLevelKey)
		})
		setDeepChanges(changes)
	}

	const handleApply = () => {
		if (!selectedComponentType || deepChanges.length === 0) {
			onClose()
			return
		}

		const isSection = selectedComponentType.includes('-section')
		const componentName = selectedComponentType.replace('-section', '') as FormComponentName

		// Apply to all components of this type - applying only the specific deep changes
		const applyToComponents = (components: typeof formComponents): void => {
			components.forEach((comp) => {
				const compIsSection = (comp.custom as { isSection?: boolean })?.isSection

				// For container components, check both name and isSection
				// For other components, only check name
				const isMatch =
					componentName === 'container'
						? comp.name === componentName && compIsSection === isSection
						: comp.name === componentName

				if (isMatch) {
					// Apply only the deep changes to this component's properties
					const currentProps = comp.custom as Record<string, unknown>
					const updatedProps = applyDeepChanges(currentProps, deepChanges)

					updateComponentProps(comp.id, updatedProps)
				}
				if (comp.children && comp.children.length > 0) {
					applyToComponents(comp.children)
				}
			})
		}

		applyToComponents(formComponents)

		// Reset and close
		setSelectedComponentType('')
		setEditedProps({})
		setInitialProps({})
		setDeepChanges([])
		onClose()
	}

	const handleClose = () => {
		setSelectedComponentType('')
		setEditedProps({})
		setInitialProps({})
		setDeepChanges([])
		onClose()
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title='Bulk Edit Components'
			buttons={[
				{
					variant: 'secondary',
					onClick: handleClose,
					children: 'Cancel',
				},
				{
					variant: 'primary',
					onClick: handleApply,
					children: 'Apply Changes',
					disabled: deepChanges.length === 0,
				},
			]}
			bodyClassName='max-h-[70vh] overflow-y-auto'>
			<div className='space-y-4'>
				<div>
					<Text variant='small' className='block mb-2 text-left'>
						Select Component Type
					</Text>
					<Select
						value={selectedComponentType}
						onChange={(val) => {
							setSelectedComponentType(val)
							// When changing component type, find a sample and use its props as the starting point
							const isSection = val.includes('-section')
							const componentName = val.replace('-section', '') as FormComponentName

							const findComponent = (components: typeof formComponents): (typeof components)[0] | null => {
								for (const comp of components) {
									const compIsSection = (comp.custom as { isSection?: boolean })?.isSection
									if (comp.name === componentName && compIsSection === isSection) {
										return comp
									}
									if (comp.children && comp.children.length > 0) {
										const found = findComponent(comp.children)
										if (found) return found
									}
								}
								return null
							}

							const sample = findComponent(formComponents)
							const definition = getComponentDefinition(componentName, isSection)

							// Merge sample props with default props to ensure all required fields are present
							const defaultProps = definition?.defaultProps || {}
							const sampleProps = (sample?.custom as Record<string, unknown>) || {}
							const props = { ...defaultProps, ...sampleProps }

							setInitialProps({ ...props })
							setEditedProps({ ...props })
							setDeepChanges([])
						}}
						options={componentTypeOptions}
						placeholder='Choose a component type to edit'
					/>
					{componentTypeOptions.length === 0 && (
						<Text variant='small' className='text-neutral-500 mt-2'>
							No editable components found in this form
						</Text>
					)}
				</div>

				{selectedComponentType && selectedDefinition && (
					<>
						<div className='h-px bg-neutral-200 my-4' />
						<Text variant='small' className='text-neutral-600 mb-2'>
							Edit properties below. Changes will be applied to all {selectedDefinition.displayName} components.
						</Text>
						<div className='space-y-3'>
							<DynamicPropertyEditor
								key={selectedComponentType}
								propertyName='props'
								propertyValue={editedProps}
								componentDefinition={selectedDefinition}
								onChange={handlePropertyChange}
							/>
						</div>
						{deepChanges.length > 0 && (
							<div className='mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md'>
								<Text variant='small' className='text-blue-800 font-semibold'>
									{deepChanges.length} change{deepChanges.length === 1 ? '' : 's'} will be applied:
								</Text>
								<div className='mt-2 space-y-1'>
									{deepChanges.map((change) => (
										<Text key={change.path} variant='xsmall' className='text-blue-700 font-mono'>
											{change.path}
										</Text>
									))}
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</Modal>
	)
}
